<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, watch, onMounted, inject } from "vue"
import { useRoute, useRouter } from "vue-router"
import { useSourceStore } from "@core/stores/source.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useEventBus } from "@vueuse/core"
import type { Services } from "@services"
import type { BuildingSource, NotificationEvent } from "@types"
import type { WeatherStationItem } from "@core/services/source.service"
import pinDataGreyIconUrl from "@icons/map/pin-data-grey.svg?url"
import pinDataGreySquareIconUrl from "@icons/map/pin-data-grey-square.svg?url"
import pinDataBlueIconUrl from "@icons/map/pin-data-blue.svg?url"
import pinDataBlueSquareIconUrl from "@icons/map/pin-data-blue-square.svg?url"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            success_save_weather_station: "Station météo mise à jour !",
            error_fetch_weather_stations:
                "Erreur lors de la récupération des stations météo",
            error_save_weather_station_info:
                "Erreur lors de la sauvegarde de la station météo",
            header_change_reference: "Station météo de référence",
            label_current_station: "Station météo actuelle",
            label_new_station: "Nouvelle station météo",
            placeholder_new_station: "Sélectionner une station",
            btn_edit_station: "Modifier la station",
            btn_save: "Sauvegarder",
            btn_cancel: "Annuler",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Feature GeoJSON de type Point
 */
interface GeoJSONPointFeature {
    type: "Feature"
    geometry: {
        type: "Point"
        coordinates: [number, number] // [lng, lat]
    }
    properties: Record<string, unknown>
}

/**
 * FeatureCollection GeoJSON
 */
interface GeoJSONFeatureCollection {
    type: "FeatureCollection"
    features: GeoJSONPointFeature[]
}

interface Props {
    /**
     * Identifiant de la station météo (source)
     */
    sourceId: string
    /**
     * Mode éditable : affiche le formulaire de modification si true
     */
    editable?: boolean
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    editable: false,
})

// ================================
// COMPOSABLES
// ================================
const sourceStore = useSourceStore()
const buildingStore = useBuildingStore()

// Gestion défensive du router pour Storybook
const router = useRouter()
const route = useRoute()

const services = inject<Services>("services")
const bus = useEventBus<NotificationEvent>("notification")

const { getSourceById } = storeToRefs(sourceStore)
const { building } = storeToRefs(buildingStore)

// ================================
// ÉTAT
// ================================
/**
 * Liste des stations météo disponibles
 */
const weatherStations = ref<WeatherStationItem[]>([])

/**
 * Identifiant de la station sélectionnée dans le select
 */
const selectedStationId = ref<string>("")

/**
 * Identifiant de la source actuelle (utilisé pour gérer les cas où le router n'est pas disponible)
 * Initialisé avec props.sourceId et mis à jour lors de la sauvegarde si le router n'est pas disponible
 */
const currentSourceId = ref<string>(props.sourceId)

/**
 * Indique si une modification est en cours
 */
const isModifying = ref<boolean>(false)

/**
 * Indique si le formulaire est en cours de soumission
 */
const isSubmitting = ref<boolean>(false)

/**
 * Indique si la liste des stations météo est en cours de chargement
 */
const isLoadingStations = ref<boolean>(false)

/**
 * Mode édition : sélecteur + sauvegarde (après clic sur « Modifier la station »)
 */
const isReferenceEditOpen = ref<boolean>(false)

// ================================
// DONNÉES CALCULÉES
// ================================
/**
 * Identifiant du bâtiment (depuis la route query ou le store)
 */
const getBuildingId = computed<string | undefined>(() => {
    // Priorité au store building
    if (building.value?._id) {
        return building.value._id
    }
    // Fallback sur la route query si disponible
    if (route?.query?.buildingId) {
        return route.query.buildingId as string
    }
    return undefined
})

/**
 * Source actuelle (station météo)
 */
const getCurrentSource = computed(() => {
    if (!currentSourceId.value) return undefined
    return getSourceById.value(currentSourceId.value)
})

/**
 * Nom de la station météo actuelle
 */
const getCurrentStationName = computed(() => {
    if (!getCurrentSource.value) {
        return ""
    }
    const name = getCurrentSource.value.name
    const idsource = getCurrentSource.value.idsource
    const result = name ? `${name} - ${idsource}` : idsource
    return result
})

/**
 * Options pour le select des stations météo
 */
const getStationOptions = computed(() => {
    return weatherStations.value
        .filter((station) => station._id !== currentSourceId.value)
        .map((station) => ({
            label: station.name
                ? `${station.name} - ${station.idsource}`
                : station.idsource,
            value: station._id,
        }))
        .sort((a, b) => {
            const textA = a.label.toUpperCase()
            const textB = b.label.toUpperCase()
            return textA < textB ? -1 : textA > textB ? 1 : 0
        })
})

/**
 * Indique si une modification a été effectuée
 */
const getHasModification = computed(() => {
    return (
        selectedStationId.value !== "" &&
        selectedStationId.value !== currentSourceId.value
    )
})

/**
 * Centre de la carte (position de la station actuelle ou par défaut)
 */
const getMapCenter = computed<[number, number]>(() => {
    const currentStation = weatherStations.value.find(
        (station) => station._id === currentSourceId.value
    )
    if (currentStation?.position) {
        const [lng, lat] = currentStation.position
        return [lat, lng]
    }
    return [48.8566, 2.3522] // Paris par défaut
})

/**
 * Indique si le centre de la carte correspond à une station connue (pas le fallback Paris)
 */
const getHasStationPosition = computed(() => {
    const currentStation = weatherStations.value.find(
        (station) => station._id === currentSourceId.value
    )
    return !!currentStation?.position
})

/**
 * Niveau de zoom : rapproché sur la station quand on a sa position, sinon vue large
 */
const getMapZoom = computed(() => {
    return getHasStationPosition.value ? 12 : 5
})

/**
 * GeoJSON pour la carte avec toutes les stations météo
 */
const getMapGeoJSON = computed<GeoJSONFeatureCollection>(() => {
    const features: GeoJSONPointFeature[] = weatherStations.value.map(
        (station) => {
            const [lng, lat] = station.position
            const isCurrent = station._id === currentSourceId.value
            const isSelected = station._id === selectedStationId.value

            // Si une autre station est sélectionnée : elle s'affiche en bleu, l'actuelle en gris. Sinon l'actuelle reste en bleu.
            const isHighlighted = selectedStationId.value
                ? isSelected
                : isCurrent
            let iconUrl: string
            let legendName: string
            let legendIcon: string

            if (station.operator !== "meteofrance") {
                iconUrl = isHighlighted
                    ? pinDataBlueSquareIconUrl
                    : pinDataGreySquareIconUrl
                legendName = "Autres stations"
                legendIcon = pinDataGreySquareIconUrl
            } else {
                iconUrl = isHighlighted
                    ? pinDataBlueIconUrl
                    : pinDataGreyIconUrl
                legendName = "Station Météo France"
                legendIcon = pinDataGreyIconUrl
            }

            return {
                type: "Feature" as const,
                geometry: {
                    type: "Point" as const,
                    coordinates: [lng, lat] as [number, number],
                },
                properties: {
                    _id: station._id,
                    idsource: station.idsource,
                    operator: station.operator,
                    name: station.name
                        ? `${station.name} - ${station.idsource}`
                        : station.idsource,
                    icon: iconUrl,
                    legendName,
                    legendIcon,
                },
            }
        }
    )

    return {
        type: "FeatureCollection" as const,
        features,
    }
})

/**
 * Formulaire de changement de station (sélecteur, carte interactive, sauvegarde)
 */
const getShouldShowForm = computed(() => {
    if (!props.editable) return false
    return !!getBuildingId.value && isReferenceEditOpen.value
})

/**
 * Aperçu lecture seule + bouton pour ouvrir le mode édition
 */
const getShouldShowEditablePreview = computed(() => {
    if (!props.editable) return false
    return !!getBuildingId.value && !isReferenceEditOpen.value
})

// ================================
// LOGIQUE MÉTIER
// ================================
/**
 * Récupère la liste des stations météo disponibles
 */
async function fetchWeatherStations(): Promise<void> {
    if (!services?.$getWeatherStations) {
        console.error("Service $getWeatherStations non disponible")
        return
    }

    isLoadingStations.value = true
    try {
        weatherStations.value = await services.$getWeatherStations()
    } catch (error) {
        console.error(
            "Erreur lors de la récupération des stations météo:",
            error
        )
        bus.emit({
            severity: "error",
            title: t("error_fetch_weather_stations"),
            message: "",
        })
    } finally {
        isLoadingStations.value = false
    }
}

/**
 * Gère le clic sur un marqueur de la carte
 * @param feature - Feature GeoJSON du marqueur cliqué
 */
function onSelectMarker(feature: GeoJSONPointFeature): void {
    if (!props.editable) return
    if (!isReferenceEditOpen.value) return
    if (isSubmitting.value) return

    const stationId = feature.properties._id as string
    if (stationId === currentSourceId.value) {
        // Si on clique sur la station actuelle, on annule la sélection
        selectedStationId.value = ""
        isModifying.value = false
    } else {
        selectedStationId.value = stationId
        isModifying.value = true
    }
}

/**
 * Annule la modification en cours
 */
function onCancelModification(): void {
    selectedStationId.value = ""
    isModifying.value = false
    isReferenceEditOpen.value = false
}

/**
 * Sauvegarde la modification de la station météo
 */
async function onSaveModification(): Promise<void> {
    if (isSubmitting.value) return
    if (!selectedStationId.value || !getBuildingId.value) return

    const newStation = weatherStations.value.find(
        (s) => s._id === selectedStationId.value
    )

    if (!newStation) {
        console.error(
            "[WeatherStationDisplay] New station not found:",
            selectedStationId.value
        )
        bus.emit({
            severity: "error",
            title: t("error_save_weather_station_info"),
            message: "",
        })
        return
    }

    isSubmitting.value = true

    const oldSourceId = currentSourceId.value

    try {
        await buildingStore.updateBuildingWeatherStation({
            _id_building: getBuildingId.value,
            newSourceId: selectedStationId.value,
        })

        const buildingSource = buildingStore.building?.sources?.find(
            (s) => s._id_source === selectedStationId.value
        ) as BuildingSource | undefined

        sourceStore.syncWeatherStationSourceAfterBuildingUpdate({
            oldSourceId,
            newSourceId: selectedStationId.value,
            buildingSource,
            weatherItem: newStation,
        })

        currentSourceId.value = selectedStationId.value

        // Met à jour la route pour refléter la nouvelle source

        if (router && route.params?._id_source) {
            await router.replace({
                params: {
                    ...route.params,
                    _id_source: selectedStationId.value,
                },
                query: route.query,
            })
        } else if (
            router &&
            typeof route.path === "string" &&
            route.path.includes("/station-meteo")
        ) {
            await router.replace({
                path: route.path,
                query: {
                    ...route.query,
                    _id_source: selectedStationId.value,
                },
            })
        } else {
            console.warn("[WeatherStationDisplay] Cannot update route:", {
                hasRouter: !!router,
                hasRoute: !!route,
                routeParams: route?.params,
                routePath: route?.path,
            })
            currentSourceId.value = selectedStationId.value
        }

        bus.emit({
            severity: "success",
            title: t("success_save_weather_station"),
            message: "",
        })

        selectedStationId.value = ""
        isModifying.value = false
        isReferenceEditOpen.value = false
    } catch (error) {
        console.error("[WeatherStationDisplay] Error during save:", error)
        bus.emit({
            severity: "error",
            title: t("error_save_weather_station_info"),
            message: "",
        })
    } finally {
        isSubmitting.value = false
    }
}

// ================================
// WATCHERS
// ================================
/**
 * Surveille les changements de props.sourceId et met à jour currentSourceId
 */
watch(
    () => props.sourceId,
    (newId) => {
        if (newId && newId !== currentSourceId.value) {
            currentSourceId.value = newId
        }
        isReferenceEditOpen.value = false
        selectedStationId.value = ""
        isModifying.value = false
    },
    { immediate: true }
)

/**
 * Surveille les changements de selectedStationId pour mettre à jour isModifying
 */
watch(
    () => selectedStationId.value,
    (newValue) => {
        isModifying.value =
            newValue !== "" && newValue !== currentSourceId.value
    }
)

// ================================
// LIFECYCLE
// ================================
onMounted(async () => {
    await fetchWeatherStations()

    // Charge le bâtiment si on a un buildingId
    if (getBuildingId.value && !building.value) {
        await buildingStore.fetchBuilding(getBuildingId.value)
    }
})
</script>

<template>
    <BaseTitledCard>
        <!-- ================================ -->
        <!-- HEADER (si mode éditable) -->
        <!-- ================================ -->
        <template #header>
            {{ t("header_change_reference") }}
        </template>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template #content>
            <!-- ================================ -->
            <!-- APERÇU (ÉDITABLE : BOUTON POUR OUVRIR LE FORMULAIRE) -->
            <!-- ================================ -->
            <div
                v-if="getShouldShowEditablePreview"
                class="flex flex-col gap-6"
            >
                <fieldset class="flex flex-col gap-4 border-none p-0">
                    <BaseInputText
                        :model-value="getCurrentStationName"
                        readonly
                        :label="t('label_current_station')"
                        class="w-full"
                    />
                </fieldset>

                <fieldset class="flex flex-col gap-4 border-none p-0 mt-4">
                    <BaseMap
                        :geojson="getMapGeoJSON"
                        :center="getMapCenter"
                        :zoom="getMapZoom"
                        :min-zoom="3"
                        :auto-fit-bounds="false"
                        :loading="isLoadingStations"
                        height="33.375rem"
                        class="w-full rounded-md"
                    />
                </fieldset>

                <div class="flex justify-end mt-4">
                    <BaseButton
                        type="button"
                        :label="t('btn_edit_station')"
                        icon="pi pi-pencil"
                        @click="isReferenceEditOpen = true"
                    />
                </div>
            </div>

            <!-- ================================ -->
            <!-- FORMULAIRE DE MODIFICATION -->
            <!-- ================================ -->
            <form
                v-else-if="getShouldShowForm"
                @submit.prevent="onSaveModification"
                class="flex flex-col gap-6"
            >
                <fieldset class="flex flex-col gap-4 border-none p-0">
                    <BaseInputText
                        :model-value="getCurrentStationName"
                        readonly
                        :label="t('label_current_station')"
                        class="w-full"
                    />

                    <BaseSelect
                        v-model="selectedStationId"
                        :options="getStationOptions"
                        option-label="label"
                        option-value="value"
                        :disabled="isSubmitting"
                        :label="t('label_new_station')"
                        :placeholder="t('placeholder_new_station')"
                        class="w-full"
                    />
                </fieldset>

                <!-- ================================ -->
                <!-- CARTE DES STATIONS -->
                <!-- ================================ -->
                <fieldset class="flex flex-col gap-4 border-none p-0 mt-4">
                    <BaseMap
                        :geojson="getMapGeoJSON"
                        :center="getMapCenter"
                        :zoom="getMapZoom"
                        :min-zoom="3"
                        :auto-fit-bounds="false"
                        :loading="isLoadingStations || isSubmitting"
                        height="33.375rem"
                        class="w-full rounded-md"
                        @select:marker="onSelectMarker"
                    />
                </fieldset>

                <!-- ================================ -->
                <!-- ACTIONS -->
                <!-- ================================ -->
                <div class="flex gap-4 justify-end mt-4">
                    <BaseButton
                        type="submit"
                        :label="t('btn_save')"
                        :disabled="!getHasModification || isSubmitting"
                        :loading="isSubmitting"
                        class="min-w-[120px]"
                    />
                    <BaseButton
                        v-if="!isSubmitting"
                        type="button"
                        :label="t('btn_cancel')"
                        severity="secondary"
                        @click="onCancelModification"
                        class="min-w-[120px]"
                    />
                </div>
            </form>

            <!-- ================================ -->
            <!-- AFFICHAGE EN LECTURE SEULE -->
            <!-- ================================ -->
            <div v-else class="flex flex-col gap-6">
                <fieldset class="flex flex-col gap-4 border-none p-0">
                    <BaseInputText
                        :model-value="getCurrentStationName"
                        readonly
                        :label="t('label_current_station')"
                        class="w-full"
                    />
                </fieldset>

                <!-- ================================ -->
                <!-- CARTE DES STATIONS -->
                <!-- ================================ -->
                <fieldset class="flex flex-col gap-4 border-none p-0 mt-4">
                    <BaseMap
                        :geojson="getMapGeoJSON"
                        :center="getMapCenter"
                        :zoom="getMapZoom"
                        :min-zoom="3"
                        :auto-fit-bounds="false"
                        :loading="isLoadingStations"
                        height="33.375rem"
                        class="w-full rounded-md"
                    />
                </fieldset>
            </div>
        </template>
    </BaseTitledCard>
</template>
<style scoped></style>
